'use client';

import type { CERConfig } from '@slims/shared/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useContextOrg } from '@/hooks/use-context-org';
import { fetchScopedConfig, scopedConfigKey } from '@/hooks/use-scoped-config';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { useAuthStore } from '@/stores/auth-store';
import { useCERStore } from '@/stores/cer-store';

type FullConfig = Omit<CERConfig, 'id' | 'tenantId'>;

/**
 * Reads the context CER configuration from the store. Uses granular selectors so
 * a consumer only re-renders when the slice it reads changes — and never just
 * because `isLoading` toggled elsewhere. The fetch + realtime sync lives in
 * {@link useCERConfigSync}, mounted once at the app shell, so this hook does no
 * I/O and pages don't each trigger a duplicate config fetch.
 */
export function useCERConfig() {
	const config = useCERStore((s) => s.config);
	const isLoading = useCERStore((s) => s.isLoading);
	const setConfig = useCERStore((s) => s.setConfig);

	return { config, isLoading, setConfig };
}

/**
 * Side-effect-only companion: keeps `useCERStore` in sync with the context org's
 * CER config. Mount exactly once (app shell).
 *
 * ADR-006: reads through the shared `['scoped-config', orgId]` React Query entry
 * — the same one `useScopedConfig` uses — so it does not fire its own
 * `/api/cer` request; it only mirrors the cached result into the zustand store.
 */
export function useCERConfigSync() {
	const contextOrg = useContextOrg();
	const loaded = useAuthStore((s) => s.loaded);
	const setFullConfig = useCERStore((s) => s.setFullConfig);
	const setLoading = useCERStore((s) => s.setLoading);
	const queryClient = useQueryClient();

	const orgId = contextOrg?.orgId ?? null;
	const tenantId = contextOrg?.tenantId ?? null;

	const { data, isLoading } = useQuery({
		queryKey: scopedConfigKey(orgId),
		enabled: loaded && !!orgId,
		queryFn: () => fetchScopedConfig(orgId as string),
	});

	// Mirror the cached config into the store the onboarding pages read from.
	useEffect(() => {
		if (data) setFullConfig(data as FullConfig);
	}, [data, setFullConfig]);

	useEffect(() => {
		setLoading(loaded && !!orgId ? isLoading : false);
	}, [isLoading, loaded, orgId, setLoading]);

	// The summary is derived: changes to source rows invalidate it too.
	useEffect(() => {
		if (!orgId || !tenantId) return;
		const subscriptions = [
			'organizations',
			'members',
			'pods',
			'meters',
			'production_plants',
			'meter_typical_profile',
		].map((table) =>
			subscribeToTable(
				`configuration-totals-${table}-${tenantId}`,
				{
					event: '*',
					schema: 'public',
					table,
					filter: `tenant_id=eq.${tenantId}`,
				},
				() => {
					void queryClient.invalidateQueries({ queryKey: scopedConfigKey(orgId) });
				},
			),
		);
		return () => {
			for (const unsubscribe of subscriptions) unsubscribe();
		};
	}, [orgId, tenantId, queryClient]);
}
